import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ElementRef, Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import type { GoogleMap } from '@angular/google-maps';
import { WINDOW } from '@ng-web-apis/common';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { trustedTypesPolicy } from 'src/app/common/utils/helpers';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapService implements OnDestroy {
  private static sharedMapLoadedSubject = new BehaviorSubject<boolean>(false);
  mapLoadedSubject = GoogleMapService.sharedMapLoadedSubject;
  apiLoaded: Observable<boolean>;
  zoomin: Subscription = new Subscription();
  zoomout: Subscription = new Subscription();

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: Window,
  ) {
    this.apiLoaded = this.mapLoadedSubject.asObservable();
  }

  loadApi() {
    // register google maps
    if (!this.mapLoadedSubject.value && isPlatformBrowser(this.platformId)) {
      const policy = trustedTypesPolicy(this.window);
      (this.window as any).initMap = () => {
        this.document.getElementById('google_maps').setAttribute('data-loaded', 'true');
        if (!this.document.getElementById('sharry_markerclustererplus')) {
          const mScript = this.document.createElement('script');
          mScript.id = 'sharry_markerclustererplus';
          mScript.src = './assets/scripts/google-map.markerclustererplus.min.js';
          mScript.defer = true;
          mScript.async = true;
          this.document.body.insertAdjacentElement('beforeend', mScript);
          mScript.onload = () => {
            this.mapLoadedSubject.next(true);
          };
        } else {
          this.mapLoadedSubject.next(isPlatformBrowser(this.platformId));
        }
      };
      if (!this.document.getElementById('google_maps')) {
        const mapsScript = this.document.createElement('script');
        const mapsScriptSrc = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&callback=initMap&loading=async&language=it&region=IT&v=3.65`;
        mapsScript.id = 'google_maps';
        mapsScript.type = 'text/javascript';
        mapsScript.defer = true;
        mapsScript.async = true;
        mapsScript.src = policy ? policy.createScriptURL(mapsScriptSrc) : mapsScriptSrc;
        this.document.head.insertAdjacentElement('beforeend', mapsScript);
      } else if (this.document.getElementById('google_maps')?.getAttribute('data-loaded') === 'true') {
        (this.window as any).initMap();
      }
    } else {
      this.mapLoadedSubject.next(isPlatformBrowser(this.platformId));
    }
  }

  zoomControl(
    div: HTMLDivElement,
    googleMap: GoogleMap,
    plusTemplate: ElementRef<HTMLElement>,
    minusTemplate: ElementRef<HTMLElement>,
  ) {
    plusTemplate.nativeElement.style.display = 'block';
    minusTemplate.nativeElement.style.display = 'block';
    const controlDiv = div;
    controlDiv.classList.add('google-map__zoom-control');

    const zoomin = this.document.createElement('div');
    zoomin.classList.add('google-map__zoom-control__button');
    zoomin.title = 'Click to zoom in';
    zoomin.appendChild(plusTemplate.nativeElement);

    const divider = this.document.createElement('div');
    divider.classList.add('google-map__zoom-control__divider');

    const zoomout = this.document.createElement('div');
    zoomout.classList.add('google-map__zoom-control__button');
    zoomout.title = 'Click to zoom out';
    zoomout.appendChild(minusTemplate.nativeElement);

    controlDiv.appendChild(zoomin);
    controlDiv.appendChild(divider);
    controlDiv.appendChild(zoomout);
    this.zoomout = fromEvent(zoomout, 'click').subscribe(() => {
      const currentZoomLevel = googleMap.getZoom();
      if (currentZoomLevel !== 0) {
        googleMap.googleMap.setZoom(currentZoomLevel - 1);
      }
    });
    this.zoomin = fromEvent(zoomin, 'click').subscribe(() => {
      const currentZoomLevel = googleMap.getZoom();
      if (currentZoomLevel !== 21) {
        googleMap.googleMap.setZoom(currentZoomLevel + 1);
      }
    });
    return controlDiv;
  }

  ngOnDestroy(): void {
    this.zoomin.unsubscribe();
    this.zoomout.unsubscribe();
  }
}
